import * as _ from "lodash";

import * as SmartDrawings from "../smartDrawings";
import { NavigationUtils } from "./navigationUtils";
import { UnitConstants } from "./constants";

/**
 * Utils for working with the viewer and element properties.
 */
export namespace ViewerUtils {
    export interface ViewerUnitData {
        modelUnit: Autodesk.Viewing.Private.ModelUnits;
        viewerUnit: Autodesk.Viewing.Private.ModelUnits; // This has to be in sync with viewer.model.getUnitString()
        modelToViewerScale: number;
        viewerToModelScale: number;
    }

    export function setModelNodeVisibility(model: Autodesk.Viewing.Model, visibility: boolean,
        viewer: Autodesk.Viewing.GuiViewer3D, ghosted: boolean = false) {
        let dbId = model.getRootId();
        if (!ghosted)
            (viewer.impl.visibilityManager as any).setNodeOff(dbId, !visibility, model);
        
        visibility ? viewer.show(dbId, model) : viewer.hide(dbId, model);
    }

    export function turnNodesOff(dbIds, viewer: Autodesk.Viewing.GuiViewer3D) {
        setVisibility(dbIds, false, viewer);
    };

    export function turnNodesOn(dbIds, viewer: Autodesk.Viewing.GuiViewer3D) {
        setVisibility(dbIds, true, viewer);
    };

    export function turnAllNodesOff(viewer: Autodesk.Viewing.GuiViewer3D) {
        turnNodesOff(getDbIds(viewer), viewer);
    }

    export function turnAllNodesOn(viewer: Autodesk.Viewing.GuiViewer3D) {
        turnNodesOn(getDbIds(viewer), viewer);
    }

    export function themeAllNodes(color, viewer: Autodesk.Viewing.GuiViewer3D) {
        for (let id of getDbIds(viewer)) {
            viewer.setThemingColor(id, color);
        }
    }

    export function baseUrl(): string {
        return [location.protocol, "//", location.host, location.pathname].join("");
    }

    export function sdUrl(): string {
        return [location.protocol, "//", location.host, "/sd"].join("");
    }

    /**
    * Check url parameter exists
    * @param paramName Name of the query string parameter
    */
    export function hasUrlParam(paramName: string): boolean {
        const urlParams = new URLSearchParams(window.location.search);
        return urlParams.has(paramName);
    }

    /**
     * Get value of the url parameter
     * @param paramName Name of the query string parameter
     */
    export function getUrlParamValue(paramName: string): string {
        const urlParams = new URLSearchParams(window.location.search);
        return urlParams.get(paramName);
    }

    /**
     * Check if url param value if true
     * If url param doesn't exist returns false
     * If it exists but has no value returns true
     */
    export function isUrlParamValueTrue(paramName: string): boolean {
        let isTrue = false;
        if (hasUrlParam(paramName)) {
            isTrue = true;
            const value = getUrlParamValue(paramName);
            if (!_.isEmpty(value)) {
                isTrue = value === "true";
            }
        }

        return isTrue;
    }

    export function setLightingEnv(
        envIndex: string, showEnvBackground: boolean, viewer: Autodesk.Viewing.GuiViewer3D) {
        // The environment setting is always persisted, so we need to set the default value when none is given.
        viewer.setLightPreset(envIndex != null ? Number(envIndex) : 1);
        viewer.setEnvMapBackground(showEnvBackground);
        viewer.prefs.tag("ignore-producer");
    }

    function positionVector(viewer: Autodesk.Viewing.GuiViewer3D) {
        return vectorToParams(viewer.navigation.getPosition(), "p");
    }

    function pivotVector(viewer: Autodesk.Viewing.GuiViewer3D) {
        return vectorToParams(viewer.navigation.getPivotPoint(), "pi");
    }

    function quaternion(viewer: Autodesk.Viewing.GuiViewer3D) {
        return quaternionToParams(viewer.navigation.getCamera().quaternion, "t");
    }

    export function convertToViewerUnits(value: number, fromUnits: Autodesk.Viewing.Private.ModelUnits,
        viewer: Autodesk.Viewing.GuiViewer3D): number {
        return Autodesk.Viewing.Private.convertUnits(fromUnits, viewer.model.getUnitString(), 1, value, "");
    }

    export function getUnitScaleFactor(fromUnits: Autodesk.Viewing.Private.ModelUnits,
        toUnits: Autodesk.Viewing.Private.ModelUnits): number {
        let factor = Autodesk.Viewing.Private.convertUnits(fromUnits, toUnits, 1, 1, "");
        return factor;
    }

    export const viewerUnitString = (viewer: Autodesk.Viewing.GuiViewer3D) =>
        Autodesk.Viewing.Private.ModelUnits[viewer.model.getUnitString() as keyof typeof Autodesk.Viewing.Private.ModelUnits];

    export function convertVectorToViewerUnits(vector: THREE.Vector3, fromUnits: Autodesk.Viewing.Private.ModelUnits,
        viewer: Autodesk.Viewing.GuiViewer3D): THREE.Vector3 {
        return vector.multiplyScalar(Autodesk.Viewing.Private.convertUnits(fromUnits, viewer.model.getUnitString(), 1, 1, ""));
    }

    export function convertVectorFromViewerUnits(vector: THREE.Vector3, toUnits: Autodesk.Viewing.Private.ModelUnits,
        viewer: Autodesk.Viewing.GuiViewer3D): THREE.Vector3 {
        return vector.multiplyScalar(Autodesk.Viewing.Private.convertUnits(viewer.model.getUnitString(), toUnits, 1, 1, ""));
    }

    export function viewStateUrl(viewer: Autodesk.Viewing.GuiViewer3D, options?): string {
        const { position, pivot, selection, isolation, env } =
            options || { position: true, pivot: true, selection: true, isolation: true, comments: true, env: true };

        const url = baseUrl();
        const paramParts = [];

        if (position) {
            paramParts.push(`${positionVector(viewer)}&${quaternion(viewer)}`);
        }

        if (pivot) {
            paramParts.push(pivotVector(viewer));
        }

        const selected = selection ? viewer.getSelection() : null;
        if (!_.isEmpty(selected)) {
            paramParts.push(`selectedNodes=${selected}`);
        }

        const isolated = isolation ? viewer.getIsolatedNodes() : null;
        if (!_.isEmpty(isolated)) {
            paramParts.push(`isolatedNodes=${isolated}`);
        }

        const environment = env ? (viewer.impl as any).currentLightPreset() : null;
        if (environment != null && environment != 1) {
            paramParts.push(`env=${environment}`);
        }

        return _.isEmpty(paramParts) ? url : `${url}?${paramParts.join("&")}`;
    }

    export function viewShareUrl(shareId: string): string {
        return `${baseUrl()}?shareId=${shareId}`;
    }

    export function applyViewState(viewer: Autodesk.Viewing.GuiViewer3D, viewParameters: SmartDrawings.ViewParameters) {
       
        // The threeJS camera doesn't have an isPerspective property but the viewer hackily injects it there and it's the only way to
        // find out the projection, so we'll use it.
        if (viewer.navigation.getCamera().isPerspective) {
            
            if (viewParameters.posVector != null) {
                viewer.navigation.setPosition(new THREE.Vector3(
                    viewParameters.posVector.x,
                    viewParameters.posVector.y,
                    viewParameters.posVector.z).toViewerUnits());
            }
            
            if (viewParameters.pivotVector != null) {
                viewer.navigation.setPivotPoint(new THREE.Vector3(
                    viewParameters.pivotVector.x,
                    viewParameters.pivotVector.y,
                    viewParameters.pivotVector.z).toViewerUnits());
            }

            if (viewParameters.targetVector != null) {
                viewer.navigation.setTarget(new THREE.Vector3(
                    viewParameters.targetVector.x,
                    viewParameters.targetVector.y,
                    viewParameters.targetVector.z).toViewerUnits());
            }

            if (viewParameters.rotationQuaternion != null) {
                const targetAndUp = NavigationUtils.quaternionToTargetAndUpVectors(viewParameters, viewer);

                viewer.navigation.setCameraUpVector(targetAndUp.up);
                viewer.navigation.setTarget(targetAndUp.target);
            }
        }
    }

    export function getLeafNodes(dbIds: number | number[], viewer: Autodesk.Viewing.GuiViewer3D) {
        const instanceTree = viewer.model.getInstanceTree();
        dbIds = dbIds || instanceTree.getRootId();

        const dbIdArray = Array.isArray(dbIds) ? dbIds : [dbIds];
        const leafIds = [];

        const getLeafNodesRec = (id) => {
            var childCount = 0;
            instanceTree.enumNodeChildren(id,
                (childId) => {
                    getLeafNodesRec(childId);
                    ++childCount;
                });
            if (childCount === 0) {
                leafIds.push(id);
            }
        }
        for (let id of dbIdArray) {
            getLeafNodesRec(id);
        }

        return leafIds;
    }

    function getDbIds(viewer: Autodesk.Viewing.GuiViewer3D) {
        const instanceTree = viewer.model.getInstanceTree();
        return Object.keys(instanceTree.nodeAccess.dbIdToIndex).map(id => Number(id));
    }

    function setVisibility(dbIds, visible: boolean, viewer: Autodesk.Viewing.GuiViewer3D) {
        if (Array.isArray(dbIds)) {
            for (let i = 0; i < dbIds.length; i++) {
                const id = dbIds[i];
                viewer.impl.visibilityManager.setNodeOff(id, !visible);
            }
        } else {
            viewer.impl.visibilityManager.setNodeOff(dbIds, !visible);
        }
    }

    export function vectorToParams(vector: THREE.Vector3, suffix: string): string {
        return makeParam(`x${suffix}`, vector.x.toFixed(2)) +
            "&" +
            makeParam(`y${suffix}`, vector.y.toFixed(2)) +
            "&" +
            makeParam(`z${suffix}`, vector.z.toFixed(2));
    }

    function quaternionToParams(quaternion: THREE.Quaternion, suffix: string): string {
        return makeParam(`x${suffix}`, quaternion.x.toFixed(2)) +
            "&" +
            makeParam(`y${suffix}`, quaternion.y.toFixed(2)) +
            "&" +
            makeParam(`z${suffix}`, quaternion.z.toFixed(2)) +
            "&" +
            makeParam(`w${suffix}`, quaternion.w.toFixed(2));
    }

    export function vectorToObject(vector: THREE.Vector3) {
        return {
            x: Number(vector.x.toFixed(2)),
            y: Number(vector.y.toFixed(2)),
            z: Number(vector.z.toFixed(2)),
        };
    }

    export function quaternionToObject(quaternion: THREE.Quaternion) {
        return {
            x: Number(quaternion.x.toFixed(2)),
            y: Number(quaternion.y.toFixed(2)),
            z: Number(quaternion.z.toFixed(2)),
            w: Number(quaternion.w.toFixed(2)),
        };
    }

    function makeParam(name: string, value: any): string {
        return name + "=" + value;
    }

    export function setLayerVisibility(layerName: string, visible: boolean, viewer) {
        const root = viewer.impl.getLayersRoot();

        if (root == null) {
            console.log("No layer information...");
            return;
        }

        const toggleLayerSub = (layer) => {
            if (layer.name === layerName) {
                viewer.setLayerVisible(
                    [layer], // array of layers
                    visible, // visible
                    false    // isolate
                );
            }
        }

        for (let i = 0; i < root.childCount; i++) {
            const layer = root.children[i];
            // We can also check inside layer groups 
            if (!layer.isLayer) {
                for (let j = 0; j < layer.childCount; j++) {
                    toggleLayerSub(layer.children[j]);
                }
            } else {
                toggleLayerSub(layer);
            }
        }
    }

    function getPropertiesPromise(dbId: number, viewer: Autodesk.Viewing.GuiViewer3D): Promise<Autodesk.Viewing.PropertyResult> {
        return new Promise((resolve, reject) => viewer.getProperties(dbId,
            props => resolve(props),
            () => reject()));
    }

    export async function getNodeProperties(dbId: number, viewer: Autodesk.Viewing.GuiViewer3D): Promise<Autodesk.Viewing.PropertyResult> {
        return await getPropertiesPromise(dbId, viewer);
    }

    export function findProperty(properties: Autodesk.Viewing.Property[],
        displayCategory: string,
        displayName: string): Autodesk.Viewing.Property {
        return _.find(properties, p => p.displayCategory === displayCategory && p.displayName === displayName);
    }

    export async function getNodeParent(dbId: number, viewer: Autodesk.Viewing.GuiViewer3D) {
        const props = await getNodeProperties(dbId, viewer) as any;
        const parentProp = _.find(props.properties, p => p.displayName === "parent");
        return parentProp != null ? parentProp.displayValue : null;
    }

    function selectorMatches(property: Autodesk.Viewing.Property, selectors: SmartDrawings.ParentSelector[]) {
        return _.some(selectors,
            s => property.displayCategory === s.displayCategory &&
            property.displayName === s.displayName &&
            property.displayValue === s.displayValue);
    } 

    export async function findNodeMatchingProp(dbId, selectors: SmartDrawings.ParentSelector[], viewer) {
        const propResult: any = await ViewerUtils.getNodeProperties(dbId, viewer);
        const prop = _.find(propResult.properties, p => selectorMatches(p, selectors));
        if (prop != null) {
            return dbId;
        }
        const parentProp = _.find(propResult.properties, p => p.displayName === "parent");
        if (parentProp != null) {
            return findNodeMatchingProp(Number(parentProp.displayValue), selectors, viewer);
        }
        return null;
    }

    export function getModel(modelId: number, viewer: Autodesk.Viewing.GuiViewer3D): Autodesk.Viewing.Model {
        return viewer.getAllModels().find(m => m.id === modelId);
    }
}